import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { MessageCircleHeart, Bug, Lightbulb, Heart, MessageSquare, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { track } from '../lib/analytics';
import { getRecentConsoleErrors } from '../lib/consoleErrorBuffer';

// FeedbackPill - a small, warm, always-available "Psst" pill anchored
// at the bottom-right of every authenticated page. Opens a lightweight
// feedback modal designed for BrainSync's earliest users: warm, human
// copy up top, low-friction form below. Auto-captures user + page +
// device context on the client so we never have to ask the user for it.
//
// This first cut is UI-only: the submit handler shows a success state
// and fires a `feedback_submitted` GA4 event, but does NOT yet POST to
// the backend - so we can iterate on the copy/UX with the founder
// before wiring up the storage + notification pipeline.

const FEEDBACK_TYPES = [
    { id: 'idea', label: 'Idea', icon: Lightbulb },
    { id: 'bug', label: 'Bug', icon: Bug },
    { id: 'praise', label: 'Praise', icon: Heart },
    { id: 'other', label: 'Other', icon: MessageSquare },
];

const DISMISS_KEY = 'bs_feedback_pill_dismissed_session';

export const FeedbackPill = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [dismissedThisSession, setDismissedThisSession] = useState(false);
    const [type, setType] = useState('idea');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Session-scoped dismissal: clicking the small X hides the pill for
    // the rest of the session but it returns on the next visit so we
    // never accidentally silence the channel for good. sessionStorage
    // scoping (not localStorage) is deliberate.
    useEffect(() => {
        try {
            if (sessionStorage.getItem(DISMISS_KEY)) setDismissedThisSession(true);
        } catch (_) { /* private mode */ }
    }, []);

    // Listen for external "open feedback" triggers - fired by the
    // Support menu item in the user dropdown so we can reuse the same
    // modal (and full context capture) instead of a plain mailto link.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onOpen = () => {
            setOpen(true);
            try { track('feedback_opened', { surface: 'user_menu_support' }); } catch (_) { /* swallow */ }
        };
        window.addEventListener('bs:open-feedback', onOpen);
        return () => window.removeEventListener('bs:open-feedback', onOpen);
    }, []);

    if (!user) return null;
    // Note: `dismissedThisSession` hides the floating pill but the
    // modal + event listener stay mounted so the Support menu item
    // (which dispatches `bs:open-feedback`) still works.

    const handleDismiss = (e) => {
        e.stopPropagation();
        try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* private mode */ }
        setDismissedThisSession(true);
    };

    const handleOpen = () => {
        setOpen(true);
        try { track('feedback_opened', { surface: 'floating_pill' }); } catch (_) { /* swallow */ }
    };

    const handleClose = (nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            // Reset state after a small delay so the close animation
            // doesn't reveal the reset form flashing.
            setTimeout(() => {
                setSubmitted(false);
                setMessage('');
                setType('idea');
            }, 300);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!message.trim() || submitting) return;
        setSubmitting(true);
        // Snapshot everything useful about the moment of submission so
        // triage on our end doesn't need a back-and-forth for context.
        const context = {
            page: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
            app_surface: (() => {
                try {
                    if (typeof window === 'undefined') return 'web';
                    // Explicit shell flags stamped by the native
                    // apps' injected JS. Checked first because the
                    // iOS WKWebView doesn't always report
                    // `display-mode: standalone` reliably, and
                    // referrer-based detection breaks after any
                    // client-side navigation.
                    if (window.sessionStorage && window.sessionStorage.getItem('bs_is_ios') === '1') return 'ios';
                    if (window.sessionStorage && window.sessionStorage.getItem('bs_is_twa') === '1') return 'twa';
                    if (window.matchMedia('(display-mode: standalone)').matches) return 'twa';
                    if (document.referrer.includes('android-app://')) return 'twa';
                    return 'web';
                } catch (_) { return 'web'; }
            })(),
            // Last 30 s of JS errors from the client-side ring buffer.
            // Bounded to 20 entries on the buffer side already.
            console_errors: getRecentConsoleErrors(),
        };
        try {
            const res = await fetch('/api/feedback', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type, message: message.trim(), context }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `Failed (${res.status})`);
            }
            track('feedback_submitted', { type, app_surface: context.app_surface });
            setSubmitted(true);
        } catch (err) {
            toast.error(err.message || 'Could not send. Try again in a moment.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {/* Floating pill - bottom-right, above any mobile bottom-nav.
                Peaches out with a subtle bounce on hover; grouped X for
                session dismissal is quiet and non-competing.
                Hidden when the user has dismissed for this session -
                the modal below still mounts so external triggers work. */}
            {!dismissedThisSession && (
            <div
                className="fixed z-40 bottom-5 right-5 md:bottom-6 md:right-6 flex items-center gap-1.5 group"
                data-testid="feedback-pill"
            >
                <button
                    type="button"
                    onClick={handleOpen}
                    className="flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-white/95 backdrop-blur-md shadow-[0_10px_25px_-5px_rgba(0,0,0,0.25)] ring-1 ring-black/5 text-[13px] font-semibold text-zinc-900 hover:bg-white hover:shadow-[0_12px_30px_-8px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 transition-all"
                    aria-label="Send feedback"
                    data-testid="feedback-pill-button"
                >
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <MessageCircleHeart className="w-3.5 h-3.5" />
                    </span>
                    <span className="tracking-tight">Psst</span>
                </button>
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 w-6 h-6 rounded-full bg-white/95 backdrop-blur-md shadow ring-1 ring-black/5 flex items-center justify-center text-zinc-400 hover:text-zinc-700"
                    aria-label="Hide feedback pill for this session"
                    data-testid="feedback-pill-dismiss"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
            )}

            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="feedback-modal">
                    {!submitted ? (
                        <>
                            {/* Warm intro banner - emerald gradient, subtle
                                grain, personal salutation. Matches
                                onboarding tonality. */}
                            <div className="relative px-6 pt-7 pb-5 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 text-white overflow-hidden">
                                <div
                                    className="pointer-events-none absolute inset-0 opacity-[0.10] mix-blend-overlay"
                                    style={{
                                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
                                    }}
                                />
                                <DialogHeader className="relative">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-100/90">
                                            <MessageCircleHeart className="w-3.5 h-3.5" />
                                            Psst
                                        </span>
                                    </div>
                                    <DialogTitle className="text-[22px] font-bold leading-tight tracking-tight text-white">
                                        You&rsquo;re one of our first users
                                    </DialogTitle>
                                    <DialogDescription className="text-[13.5px] text-emerald-50/90 leading-snug mt-1">
                                        We&rsquo;d really appreciate your feedback so we can make BrainSync better &mdash; even the tiny nitpicks help. Every message goes straight to the founder.
                                    </DialogDescription>
                                </DialogHeader>
                            </div>

                            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                                {/* Type chips - single-select. Idea is the
                                    default because it's the highest-signal
                                    category at this stage; bugs go into
                                    Sentry/logs anyway. */}
                                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Feedback type">
                                    {FEEDBACK_TYPES.map((t) => {
                                        const Icon = t.icon;
                                        const active = type === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                onClick={() => setType(t.id)}
                                                className={
                                                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ' +
                                                    (active
                                                        ? 'bg-emerald-600 text-white ring-1 ring-emerald-600'
                                                        : 'bg-zinc-100 text-zinc-700 ring-1 ring-transparent hover:bg-zinc-200')
                                                }
                                                data-testid={`feedback-type-${t.id}`}
                                            >
                                                <Icon className="w-3.5 h-3.5" />
                                                {t.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                <Textarea
                                    autoFocus
                                    rows={5}
                                    placeholder="What&rsquo;s on your mind? The more specific, the better..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="resize-none"
                                    data-testid="feedback-textarea"
                                    maxLength={2000}
                                />

                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-[11px] text-zinc-500 leading-snug">
                                        We&rsquo;ll attach your account + page so we can help faster.
                                    </p>
                                    <Button
                                        type="submit"
                                        disabled={!message.trim() || submitting}
                                        className="flex-shrink-0"
                                        data-testid="feedback-submit"
                                    >
                                        {submitting ? 'Sending...' : 'Send'}
                                    </Button>
                                </div>
                            </form>
                        </>
                    ) : (
                        // Success state - warm, single line, low-key close.
                        <div className="px-6 py-10 text-center space-y-3" data-testid="feedback-success">
                            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                <Check className="w-6 h-6" strokeWidth={2.5} />
                            </div>
                            <h3 className="text-[17px] font-bold text-zinc-900 tracking-tight">Thanks &mdash; we read every one</h3>
                            <p className="text-[13.5px] text-zinc-600 leading-snug max-w-xs mx-auto">
                                Your note just landed with the founder. If it needs a reply, we&rsquo;ll email you soon.
                            </p>
                            <div className="pt-2">
                                <Button variant="outline" onClick={() => handleClose(false)} data-testid="feedback-success-close">
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default FeedbackPill;
